/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ajax from 'helpers/ajax'
import { CalendarMonthTagType } from 'types/enums/CalendarMonthTagType'
import {
    CalendarParams,
    setParams,
    setDayData,
    setDayTicketData,
    setWeekData,
    setMonthData,
    setSelectedReservation,
    updateSelectedReservationShoppingCartItems,
    addSelectedReservationShoppingCartItem,
    removeVoucherFromReservation,
    setTimeslotRow,
    setTimeslotReservationSelect,
    resetTimeslotReservationSelect,
    setSelectedReservationTimeslots,
    resetSelectedReservationTimeslots,
    setTimeslotViewMode,
    setCalendarTab,
    pushReservation,
    setReservationShoppingCartItemRow,
    setReservationTodo,
    setReservationPlans,
    setReservationCheckoutPlans,
    setSelectedReservationPackageCartItems,
    setSelectedReservationPackageCart,
    updateSelectedReservationPackageCartPrice,
    setExtraInvoiceNoteToSelectedReservation,
    setPreparedGroupResponse,
    resetPreparedGroupResponse,
    toggleMonthFilter,
    revalidateOfferCart,
    beginOfferCartRevalidation,
    setCalendarReminderScope,
    beginReminderRevalidation,
    revalidateCalendarReminder,
} from 'store/calendar/CalendarReducer'
import { PreparedGroupOrigin } from 'types/enums/PreparedGroupOrigin'
import { Game } from 'types/entities/Game'
import { Timeslot } from 'types/entities/Timeslot'
import { TimeslotViewMode } from 'types/enums/TimeslotViewMode'
import { CalendarTab } from 'types/enums/CalendarTab'
import Dates from 'utils/Dates'
import { consoleLog, parseInteger } from 'utils/utility'
import { DayInMonth } from 'pages/Front/Calendar/components/CalendarViews/CalendarMonthView'
import { ReservationPlan } from 'types/entities/ReservationPlan'
import { DEFAULT_HALL_ID } from 'helpers/config'
import { ReservationCheckoutPlan } from 'types/entities/ReservationCheckoutPlan'
import { SetReservationPackageCartItems } from 'types/entities/SetReservationPackageCartItems'
import { SetExtraInvoiceNoteToReservation } from 'types/entities/SetExtraInvoiceNoteToReservation'
import { MonthDataResponse } from 'types/responses/MonthDataResponse'
import type { Offer } from 'types/entities/Offer'
import type {Reminder} from 'types/entities/Reminder'
import type { RootState } from 'helpers/store'
import { ReservationTodo } from 'types/entities/ReservationTodo'
import { RemoveVoucherFromReservation } from 'types/entities/RemoveVoucherFromReservation'
import { Ticket } from 'types/entities/Ticket'
import { ReservationDriver } from 'types/entities/ReservationDriver'
import { createAction } from 'helpers/createAction'
import { ShoppingCartItem } from 'types/entities/ShoppingCartItem'
import { AnyRace } from 'types/entities/AnyRace'
import { DeleteTicketDetail } from 'types/entities/DeleteTicketDetail'
import { AnyRaceDetail } from 'types/app'
import { PreparedGroupTicket } from 'types/entities/PreparedGroupTicket'
import { Reservation } from 'types/entities/Reservation'
import { PackagesCart } from 'types/entities/PackagesCart'

class CalendarAction {
    static readonly SET_PARAMS = 'CalendarAction.SetParams'
    static readonly SET_DAY_TICKET_DATA = 'CalendarAction.SetDayTicketData'
    static readonly SET_DAY_DATA = 'CalendarAction.SetDayData'
    static readonly SET_WEEK_DATA = 'CalendarAction.SetWeekData'
    static readonly SET_MONTH_DATA = 'CalendarAction.SetMonthData'
    static readonly SET_SELECTED_RESERVATION =
        'CalendarAction.SetSelectedReservation'
    static readonly SET_TIMESLOT_ROW = 'CalendarAction.SetTimeslotRow'
    static readonly SET_TIMESLOT_RESERVATION_SELECT =
        'CalendarAction.SetTimeslotReservationSelect'
    static readonly SELECTED_RESERVATION_TIMESLOTS =
        'CalendarAction.SelectedReservationTimeslots'
    static readonly RESET_SELECTED_RESERVATION_TIMESLOTS =
        'CalendarAction.ResetSelectedReservationTimeslots'
    static readonly RESET_TIMESLOT_RESERVATION_SELECT =
        'CalendarAction.ResetTimeslotReservationSelect'
    static readonly SET_CALENDAR_TAB = 'CalendarAction.SetCalendarTab'
    static readonly SET_TIMESLOT_VIEW_MODE =
        'CalendarAction.SetTimeslotViewMode'
    static readonly SET_PREPARED_GROUP_RESPONSE =
        'CalendarAction.SetPreparedGroupResponse'
    static readonly RESET_PREPARED_GROUP_RESPONSE =
        'CalendarAction.ResetPreparedGroupResponse'
    static readonly PUSH_PREPARED_GROUP_TICKET =
        'CalendarAction.PushPreparedGroupTicket'
    static readonly REMOVE_TICKET_DETAIL_ROW =
        'CalendarAction.DeleteTicketDetailRow'
    static readonly SET_PREPARED_GROUP_RACE_ROW =
        'CalendarAction.SetPreparedGroupRaceRow'
    static readonly SET_PREPARED_GROUP_GAME_ROW =
        'CalendarAction.SetPreparedGroupGameRow'
    static readonly DELETE_PREPARED_GROUP_FROM_RACE =
        'CalendarAction.DeletePreparedGroupFromRace'
    static readonly DELETE_PREPARED_GROUP_FROM_GAME =
        'CalendarAction.DeletePreparedGroupFromGame'
    static readonly DELETE_RACE_DETAIL_ROW =
        'CalendarAction.DeleteRaceDetailRowFromRace'
    static readonly DELETE_GAME_DETAIL_ROW =
        'CalendarAction.DeleteGameDetailRowFromRace'
    static readonly DELETE_PREPARED_GROUP_FROM_LIST =
        'PreparedGroupsAction.DeletePreparedGroupFromList'
    static readonly PUSH_RESERVATION = 'CalendarAction.PushReservation'
    static readonly REMOVE_VOUCHER_FROM_RESERVATION =
        'CalendarAction.RemoveVoucherFromReservation'

    static readonly SET_RESERVATION_TODO = 'CalendarAction.SetReservationTodo'
    static readonly SET_RESERVATION_SHOPPING_CART_ITEM_ROW =
        'CalendarAction.SetReservationShoppingCartItemRow'
    static readonly SET_RESERVATION_PLANS = 'CalendarAction.SetReservationPlans'
    static readonly SET_RESERVATION_CHECKOUT_PLANS =
        'CalendarAction.SetReservationCheckoutPlans'
    static readonly UPDATE_SELECTED_RESERVATION_SHOPPING_CART_ITEMS =
        'CalendarAction.UpdateSelectedReservationShoppingCartItems'
    static readonly ADD_SELECTED_RESERVATION_SHOPPING_CART_ITEM =
        'CalendarAction.AddSelectedReservationShoppingCartItem'
    static readonly SET_SELECTED_RESERVATION_PACKAGE_CART_ITEMS =
        'CalendarAction.SetSelectedReservationPackageCartItems'
    static readonly SET_SELECTED_RESERVATION_PACKAGE_CART =
        'CalendarAction.SetSelectedReservationPackageCart'
    static readonly UPDATE_SELECTED_RESERVATION_PACKAGE_CART_PRICE =
        'CalendarAction.UpdateSelectedReservationPackageCartPrice'
    static readonly SET_EXTRA_INVOICE_NOTE_TO_SELECTED_RESERVATION =
        'CalendarAction.SetExtraInvoiceNoteToSelectedReservation'
    static readonly TOGGLE_MONTH_FILTER = 'CalendarAction.ToggleMonthFilter'

    static getDayData(
        year: number,
        month: number,
        day: number,
        hall_id?: number
    ) {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .get('/calendar/day', {
                    params: {
                        year,
                        month,
                        day,
                        hall_id: hall_id ? hall_id : DEFAULT_HALL_ID,
                        seller_id,
                    },
                })
                .then((r) => {
                    dispatch(setDayData(r))
                    // dispatch(AdminReservationsAction.setAdminReservationsList(r.reservations));
                })
        }
    }

    static getDayTicketData() {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .get('/calendar/day-tickets', {
                    params: { seller_id },
                })
                .then((r) => {
                    dispatch(setDayTicketData(r.tickets))
                })
        }
    }

    static fetchReservationDayData(monthDay: DayInMonth) {
        return this.getReservationDayData(
            parseInteger(Dates.ymd(monthDay.day, 'YYYY')),
            parseInteger(Dates.ymd(monthDay.day, 'MM')),
            parseInteger(Dates.ymd(monthDay.day, 'DD'))
        )
    }

    static getReservationDayData(year: number, month: number, day: number) {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .get('/calendar/reservations/day', {
                    params: {
                        year,
                        month,
                        day,
                        seller_id,
                    },
                })
                .then((r) => {
                    return r
                })
        }
    }

    static getWeekData(
        year: number,
        month: number,
        day: number,
        hall_id?: number
    ) {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .get('/calendar/week', {
                    params: {
                        year,
                        month,
                        day,
                        hall_id: hall_id ? hall_id : DEFAULT_HALL_ID,
                        seller_id,
                    },
                })
                .then((r) => {
                    dispatch(setWeekData(r))
                })
        }
    }

    static getMonthData(
        year: number,
        month: number,
        day: number,
        hall_id?: number
    ) {
        return async (dispatch, getState) => {
            try {
                const seller_id = getState().sessionState.session?.uid
                const response: MonthDataResponse = await ajax.get(
                    '/calendar/month',
                    {
                        params: {
                            year,
                            month,
                            day,
                            hall_id: hall_id ? hall_id : DEFAULT_HALL_ID,
                            seller_id,
                        },
                    }
                )

                // The month view is rendered from its day grid. A month without
                // events is still a valid response and must not leave the UI in
                // its loading state.
                if (Array.isArray(response?.days)) {
                    dispatch(setCalendarReminderScope({sellerId: Number(seller_id), hallId: hall_id || DEFAULT_HALL_ID}))
                    dispatch(setMonthData(response))
                    // New reminders can be missing from the cached month entirely.
                    const reminderIds = Object.entries((getState() as RootState).calendar.confirmedReminders)
                        .filter(([, entry]) => !entry.deleted).map(([id]) => id)
                    await Promise.allSettled(reminderIds.map(async id => {
                        dispatch(beginReminderRevalidation(Number(id)))
                        const revision = (getState() as RootState).calendar.confirmedReminders[Number(id)].revision
                        const detail: {reminder?: Reminder} = await ajax.get(`/admin/reminder/detail/${id}`)
                        if (detail.reminder?.id === Number(id)) dispatch(revalidateCalendarReminder({reminder: detail.reminder, revision}))
                    }))
                    // The month endpoint can lag behind successful item saves.
                    // Revalidate only locally changed offers through their detail
                    // endpoint, so another user's later edits are not masked.
                    const confirmed = (getState() as RootState).calendar.confirmedOfferCarts
                    const ids = new Set(response.days.flatMap(day => (day.offers ?? []).map(offer => offer.id)))
                    await Promise.allSettled(Object.entries(confirmed).filter(([id]) => ids.has(Number(id))).map(async ([id, snapshot]) => {
                        dispatch(beginOfferCartRevalidation(Number(id)))
                        const revision = (getState() as RootState).calendar.confirmedOfferCarts[Number(id)].revision
                        const detail: {offer?: Offer} = await ajax.get(`/admin/offers/detail/${id}`)
                        if (detail.offer?.id !== Number(id)) return
                        const cart = [detail.offer.packagesCart, ...(detail.offer.calculations ?? []).map(calculation => calculation.packagesCart)]
                            .find(candidate => candidate?.id === snapshot.cart.id)
                        if (cart) dispatch(revalidateOfferCart({offerId: Number(id), revision, cart}))
                    }))
                    return Promise.resolve(response)
                }

                consoleLog('getMonthData - reject', response)
                return Promise.reject(response)
            } catch (err) {
                consoleLog('getMonthData - error', err)
                return Promise.reject(err)
            }
        }
    }

    static createPreparedGroupFromTickets(
        tickets: Ticket[],
        ticket_codes?: string[],
        origin?: PreparedGroupOrigin,
        reservation_id?: number
    ) {
        const ticket_ids = tickets.map((ticket) => ticket.id) || []
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .post('/calendar/create-prepared-group-from-tickets', {
                    ticket_ids,
                    ticket_codes,
                    origin,
                    reservation_id,
                    seller_id,
                })
                .then((r) => {
                    dispatch(setPreparedGroupResponse(r))
                    // dispatch(ReservationAction.setPreparedGroupResponse(r));
                    return r
                })
        }
    }

    static createPreparedGroupFromReservationDrivers(
        reservationDrivers: ReservationDriver[],
        origin?: PreparedGroupOrigin,
        reservation_id?: number
    ) {
        const reservation_driver_ids =
            reservationDrivers.map(
                (reservationDriver) => reservationDriver.id
            ) || []
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .post(
                    '/calendar/create-prepared-group-from-reservation-drivers',
                    {
                        reservation_driver_ids,
                        origin,
                        reservation_id,
                        seller_id,
                    }
                )
                .then((r) => {
                    dispatch(setPreparedGroupResponse(r))
                    // dispatch(ReservationAction.setPreparedGroupResponse(r));
                    return r
                })
        }
    }

    static createNewTimeslot(
        timeslot_id: number,
        type: 'before' | 'after'
    ): (dispatch: any, getState: any) => Promise<Timeslot> {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .post('/calendar/create-new-timeslot/' + timeslot_id, {
                    type,
                    seller_id,
                })
                .then(
                    (r) => {
                        return r
                    },
                    (rej) => {
                        return rej
                    }
                )
        }
    }

    static fetchPreparedGroup(prepared_group_id: number) {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .get('/calendar/prepared-group/' + prepared_group_id, {
                    params: { seller_id },
                })
                .then((r) => {
                    dispatch(setPreparedGroupResponse(r))
                    // dispatch(ReservationAction.setPreparedGroupResponse(r));
                })
        }
    }

    static modifyPreparedGroupTicketMembers(prepared_group_id: number, data) {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            data.preparedGroupDetails = data.preparedGroupDetails
                .filter((f) => f)
                .map((row) => row)
            return ajax
                .post('/calendar/modify-prepared-group/' + prepared_group_id, {
                    ...data,
                    seller_id,
                })
                .then((r) => {
                    dispatch(setPreparedGroupResponse(r))
                    // dispatch(ReservationAction.setPreparedGroupResponse(r));
                    return r
                })
        }
    }

    static fetchTimeSlot = (timeslot_id: number) => {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .get('/calendar/timeslot/' + timeslot_id, {
                    params: { seller_id },
                })
                .then((r) => {
                    return r
                })
        }
    }

    static fetchBlockTimeslot = (timeslot_id: number, willBlock: boolean) => {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax.post(
                '/calendar/timeslot/' +
                timeslot_id +
                (willBlock ? '/block' : '/unblock'),
                { seller_id }
            )
        }
    }

    static setTimeslotRow = (timeslot: Timeslot) => {
        return createAction(CalendarAction.SET_TIMESLOT_ROW, timeslot)
    }

    static setSelectedReservationTimeslots = (timeslots_ids: number[]) => {
        return createAction(
            CalendarAction.SELECTED_RESERVATION_TIMESLOTS,
            timeslots_ids
        )
    }

    static resetSelectedReservationTimeslots = () => {
        return createAction(
            CalendarAction.RESET_SELECTED_RESERVATION_TIMESLOTS,
            []
        )
    }

    static setTimeslotReservationSelect = (timeslot: Timeslot) => {
        return createAction(
            CalendarAction.SET_TIMESLOT_RESERVATION_SELECT,
            timeslot
        )
    }

    static resetTimeslotReservationSelect = () => {
        return createAction(
            CalendarAction.RESET_TIMESLOT_RESERVATION_SELECT,
            []
        )
    }

    static setTimeslotViewMode = (timeslotViewMode: TimeslotViewMode) => {
        return createAction(
            CalendarAction.SET_TIMESLOT_VIEW_MODE,
            timeslotViewMode
        )
    }

    static setCalendarTab = (calendarTab: CalendarTab) => {
        return createAction(CalendarAction.SET_CALENDAR_TAB, calendarTab)
    }

    static fetchReservation = (reservation_id: number | string) => {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .get('/calendar/reservation/' + reservation_id, {
                    params: { seller_id },
                })
                .then((r) => {
                    // dispatch(AdminReservationsAction.pushReservation(r.reservation));
                    dispatch(
                        CalendarAction.setSelectedReservation(r.reservation)
                    )
                    return r
                })
        }
    }

    static fetchReservationWithShoppingCartItems = (
        reservation_id: number | string
    ) => {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .get(
                    '/calendar/reservation/shopping-cart-items/' +
                    reservation_id,
                    {
                        params: { seller_id },
                    }
                )
                .then((r) => {
                    // dispatch(AdminReservationsAction.pushReservation(r.reservation));
                    dispatch(
                        CalendarAction.setSelectedReservation(r.reservation)
                    )
                    return r
                })
        }
    }

    static fetchSetReservationPlan = (
        reservation_id: number,
        plan: ReservationPlan[]
    ) => {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .post(
                    '/admin/reservations/set-reservation-plan/' +
                    reservation_id,
                    { plan, seller_id }
                )
                .then((r: { reservation_plan: ReservationPlan[] }) => {
                    dispatch(
                        CalendarAction.setReservationPlans(r.reservation_plan)
                    )
                    // dispatch(AdminReservationsAction.setReservationPlans(reservation_id, r.reservation_plan));
                    return r
                })
        }
    }

    static fetchSetReservationTodo = (
        reservation_id: number,
        todoId: number,
        isActive: boolean
    ) => {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .post(
                    '/admin/reservations/set-reservation-todo/' +
                    reservation_id,
                    {
                        todo_id: todoId,
                        is_active: isActive,
                        seller_id,
                    }
                )
                .then((r) => {
                    dispatch(
                        CalendarAction.setReservationTodo(r.reservationTodo)
                    )
                    return r
                })
        }
    }

    static fetchSetReservationCheckoutPlan = (
        reservation_id: number,
        checkout_plan: ReservationCheckoutPlan[]
    ) => {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .post(
                    '/admin/reservations/set-reservation-checkout-plan/' +
                    reservation_id,
                    { checkout_plan, seller_id }
                )
                .then((r) => {
                    // dispatch(AdminReservationsAction.setReservationCheckoutPlan(reservation_id, r.reservation_checkout_plan));
                    dispatch(
                        setReservationCheckoutPlans(
                            r.reservation_checkout_plan
                        )
                    )
                    return r
                })
        }
    }

    static addTicketToPreparedGroup(
        prepared_group_id: number,
        ticket_code: string
    ) {
        return (dispatch, getState) => {
            const seller_id = getState().sessionState.session?.uid
            return ajax
                .post('/prepared-groups/add-ticket-to-prepared-group', {
                    prepared_group_id,
                    ticket_code,
                    seller_id,
                })
                .then((r) => {
                    dispatch(
                        CalendarAction.pushPreparedGroupTicket(
                            r.preparedGroupTicket
                        )
                    )
                    return r
                })
        }
    }

    static setReservationTodo(todo: ReservationTodo) {
        return createAction(CalendarAction.SET_RESERVATION_TODO, todo)
    }
    static setReservationShoppingCartItemRow(item: ShoppingCartItem) {
        return createAction(
            CalendarAction.SET_RESERVATION_SHOPPING_CART_ITEM_ROW,
            item
        )
    }

    static setPreparedGroupRaceRow(race: AnyRace) {
        return createAction(CalendarAction.SET_PREPARED_GROUP_RACE_ROW, race)
    }

    static setPreparedGroupGameRow(game: Game) {
        return createAction(CalendarAction.SET_PREPARED_GROUP_GAME_ROW, game)
    }

    static deleteTicketDetailRow(ticketDetailRow: DeleteTicketDetail) {
        return createAction(
            CalendarAction.REMOVE_TICKET_DETAIL_ROW,
            ticketDetailRow
        )
    }

    static deleteRaceDetailRow(
        raceDetail: AnyRaceDetail
    ) {
        return createAction(CalendarAction.DELETE_RACE_DETAIL_ROW, raceDetail)
    }

    static setPreparedGroupResponse(preparedGroupResponse) {
        return createAction(
            CalendarAction.SET_PREPARED_GROUP_RESPONSE,
            preparedGroupResponse
        )
    }

    static resetPreparedGroupResponse() {
        return createAction(CalendarAction.RESET_PREPARED_GROUP_RESPONSE, null)
    }

    static setDayData(day_data) {
        return createAction(CalendarAction.SET_DAY_DATA, day_data)
    }

    static setDayTicketData(day_data) {
        return createAction(CalendarAction.SET_DAY_TICKET_DATA, day_data)
    }

    static setWeekData(week_data) {
        return createAction(CalendarAction.SET_WEEK_DATA, week_data)
    }

    static setMonthData(month_data: MonthDataResponse) {
        return createAction(CalendarAction.SET_MONTH_DATA, month_data)
    }

    static setSelectedReservation(reservation) {
        return createAction(
            CalendarAction.SET_SELECTED_RESERVATION,
            reservation
        )
    }

    static pushPreparedGroupTicket(ticket: PreparedGroupTicket) {
        return createAction(CalendarAction.PUSH_PREPARED_GROUP_TICKET, ticket)
    }

    static removePreparedGroupFromRace(race: AnyRace) {
        return createAction(
            CalendarAction.DELETE_PREPARED_GROUP_FROM_RACE,
            race
        )
    }

    static removePreparedGroupFromGame(game: Game) {
        return createAction(
            CalendarAction.DELETE_PREPARED_GROUP_FROM_GAME,
            game
        )
    }

    static deletePreparedGroupFromList = (prepared_group_id: number) => {
        return createAction(
            CalendarAction.DELETE_PREPARED_GROUP_FROM_LIST,
            prepared_group_id
        )
    }

    static pushReservation = (
        reservation: Reservation
    ) => {
        return createAction(CalendarAction.PUSH_RESERVATION, reservation)
    }

    static setParams = (
        params: CalendarParams
    ) => {
        return createAction(CalendarAction.SET_PARAMS, params)
    }

    static setReservationPlans(reservation_plan: ReservationPlan[]) {
        return createAction(
            CalendarAction.SET_RESERVATION_PLANS,
            reservation_plan
        )
    }

    static setReservationCheckoutPlans(
        checkoutPlans: ReservationCheckoutPlan[]
    ) {
        return createAction(
            CalendarAction.SET_RESERVATION_CHECKOUT_PLANS,
            checkoutPlans
        )
    }

    static resetReservationPlans() {
        return createAction(CalendarAction.SET_RESERVATION_PLANS, [])
    }

    static updateSelectedReservationShoppingCartItems(
        data: ShoppingCartItem[]
    ) {
        return createAction(
            CalendarAction.UPDATE_SELECTED_RESERVATION_SHOPPING_CART_ITEMS,
            data
        )
    }

    static addSelectedReservationShoppingCartItem(data: ShoppingCartItem) {
        return createAction(
            CalendarAction.ADD_SELECTED_RESERVATION_SHOPPING_CART_ITEM,
            data
        )
    }

    static setSelectedReservationPackageCartItems(
        data: SetReservationPackageCartItems
    ) {
        return createAction(
            CalendarAction.SET_SELECTED_RESERVATION_PACKAGE_CART_ITEMS,
            data
        )
    }

    static updateSelectedReservationPackageCartPrice(price: number) {
        return createAction(
            CalendarAction.UPDATE_SELECTED_RESERVATION_PACKAGE_CART_PRICE,
            price
        )
    }

    static setSelectedReservationPackageCart(data: PackagesCart) {
        return createAction(
            CalendarAction.SET_SELECTED_RESERVATION_PACKAGE_CART,
            data
        )
    }

    static setExtraInvoiceNoteToSelectedReservation(
        data: SetExtraInvoiceNoteToReservation
    ) {
        return createAction(
            CalendarAction.SET_EXTRA_INVOICE_NOTE_TO_SELECTED_RESERVATION,
            data
        )
    }

    static removeVoucherFromReservation(data: RemoveVoucherFromReservation) {
        return createAction(
            CalendarAction.REMOVE_VOUCHER_FROM_RESERVATION,
            data
        )
    }

    static toggleMonthFilter(filter: CalendarMonthTagType) {
        return toggleMonthFilter(filter)
    }
}

export default CalendarAction
